/**
 * Lightweight instrumentation singleton for capturing pipeline data during benchmarking.
 * All methods are no-ops unless window.__BENCHMARK_ENABLED__ is set to true
 * (injected by Playwright's addInitScript before the app loads).
 */

interface BenchmarkData {
  rawRecognitions: Array<{ timestamp: number; text: string }>;
  rawTranslations: Array<{ timestamp: number; lang: string; text: string }>;
  ttsQueueEntries: Array<{ timestamp: number; lang: string; text: string }>;
}

declare global {
  interface Window {
    __BENCHMARK_ENABLED__?: boolean;
    __benchmark__?: BenchmarkData;
  }
}

class BenchmarkCollector {
  private static _instance: BenchmarkCollector;
  private enabled: boolean;
  private data: BenchmarkData;

  private constructor() {
    this.enabled = typeof window !== 'undefined' && window.__BENCHMARK_ENABLED__ === true;
    this.data = { rawRecognitions: [], rawTranslations: [], ttsQueueEntries: [] };
    if (this.enabled && typeof window !== 'undefined') {
      window.__benchmark__ = this.data;
    }
  }

  static instance(): BenchmarkCollector {
    if (!BenchmarkCollector._instance) {
      BenchmarkCollector._instance = new BenchmarkCollector();
    }
    return BenchmarkCollector._instance;
  }

  captureRawRecognition(text: string): void {
    if (!this.enabled || !text) return;
    this.data.rawRecognitions.push({ timestamp: Date.now(), text });
  }

  captureRawTranslation(lang: string, text: string): void {
    if (!this.enabled || !text) return;
    this.data.rawTranslations.push({ timestamp: Date.now(), lang, text });
  }

  captureTtsQueueEntry(lang: string, text: string): void {
    if (!this.enabled || !text) return;
    this.data.ttsQueueEntries.push({ timestamp: Date.now(), lang, text });
  }

  reset(): void {
    this.data.rawRecognitions = [];
    this.data.rawTranslations = [];
    this.data.ttsQueueEntries = [];
  }
}

export default BenchmarkCollector;
